<template>
  <div>
    <areYouSureBox
      has-modal-card
      :active="showAreYouSure"
      @response="areYouSureResponseHandler"
    />
    <div class="container columns">
      <form class="column is-two-fifths">
        <b-field :label="$t('ChainId')">
          <b-input
            :value="isNew ? $t('SystemGenerated') : selectedChain.chainId?.toString()"
            disabled
          />
        </b-field>
        <b-field :label="$t('ChainName')">
          <b-input
            v-model="selectedChain.chainName"
            :validation-message="
              $t('FieldIsRequired', { fieldName: $t('ChainName') })
            "
            data-test-id="chainNameInput"
            :disabled="!canEdit"
            ref="chainNameRef"
            required
            expanded
          />
        </b-field>
        <b-field grouped position="is-right">
          <b-field>
            <b-button @click="cancelUpdate">
              {{ canEdit ? $t("Cancel") : $t("Return") }}
            </b-button>
          </b-field>
          <b-field v-if="canEdit">
            <b-button
              @click="startProcessing"
              :disabled="isProcessing"
              data-test-id="chainDetailsSubmit"
            >
              {{ $t("Submit") }}
            </b-button>
          </b-field>
        </b-field>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue'
import { useI18n } from 'vue-i18n'
import * as Sentry from '@sentry/browser'
import { useAppStore } from '@/stores/app'
import areYouSureBox from '@/components/AreYouSureBox.vue'
import shopsService from '@/services/shopsService'

// Types
interface Chain {
  chainId: number
  chainName: string
  chainExternalId?: string
  chainGroupId?: number
  chainGroupName?: string
  shopsRelated?: any[]
  groupsRelated?: any[]
}

interface ServiceChain {
  chainId: number
  chainName: string
  chainExternalId: string
  chainGroupId: number
  chainGroupName: string
  shopsRelated?: { shopId: number }[]
  groupsRelated?: any[]
}

// Props
const props = defineProps<{
  id?: string | number
}>()

// Emits
const emit = defineEmits<{
  'chain-update-cancelled': []
  'chain-update-complete': [chain: Chain]
}>()

// Composables
const { t } = useI18n()
const store = useAppStore()
const instance = getCurrentInstance()

// Refs
const chainNameRef = ref<HTMLInputElement>()

// Reactive state
const selectedChain = ref<Chain>({
  chainId: 0,
  chainName: ''
})
const originalChain = ref<Chain>({
  chainId: 0,
  chainName: ''
})
const showAreYouSure = ref(false)
const isNew = ref(false)
const processing = ref(false)
const chainUpdated = ref(false)
const canEdit = ref(true)

// Computed
const isProcessing = computed(() => processing.value)

const chainsList = computed(() => store.getChains)

// Methods
const getChainToEdit = () => {
  if (!chainsList.value) {
    selectedChain.value = {
      chainId: 0,
      chainName: ''
    }
    return
  }
  
  const chain = chainsList.value.find((x: any) => x.chainId.toString() === props.id?.toString())
  selectedChain.value = chain ? { ...chain } : {
    chainId: 0,
    chainName: ''
  }
  originalChain.value = { ...selectedChain.value }
}

const revertToOriginalChain = () => {
  Object.assign(selectedChain.value, originalChain.value)
}

const resetChain = () => {
  selectedChain.value = {
    chainId: 0,
    chainName: ''
  }
  chainUpdated.value = false
  isNew.value = false
}

const cancelUpdate = () => {
  emit('chain-update-cancelled')
}

const areYouSureResponseHandler = async (action: string) => {
  if (showAreYouSure.value) {
    showAreYouSure.value = false
    try {
      if (action === 'yes') {
        await sendUpdate()
      }
    } finally {
      processing.value = false
    }
  }
}

const createNewChain = () => {
  selectedChain.value = {
    chainId: 0,
    chainName: '',
  }
  isNew.value = true
}

const startProcessing = () => {
  if (!processing.value) {
    processing.value = true
    const validationResults = shopsService.validateChainData([chainNameRef.value])
    
    if (validationResults.length > 0) {
      processing.value = false
      const translatedErrors = validationResults
        .map((validator) => t(validator))
        .join(', ')
      openNotification(translatedErrors, 'is-danger')
    } else {
      showAreYouSure.value = true
    }
  }
}

const openNotification = (message: string, type: string) => {
  let autoCloseProps: { autoClose?: boolean; duration?: number } = {}
  
  if (type === 'is-success') {
    autoCloseProps = {
      autoClose: true,
      duration: 5000,
    }
  }

  // Access Buefy through the component instance  
  const globalProperties = instance?.appContext.config.globalProperties
  const buefy = globalProperties?.$buefy
  if (buefy?.notification) {
    const notificationOptions = {
      message,
      type,
      hasIcon: true,
      indefinite: true,
      closeable: true,
      position: 'is-top' as const,
      ...autoCloseProps,
    }
    
    // Use Object.assign to add data attribute without type issues
    Object.assign(notificationOptions, { 'data-test-id': 'chainValidationMessage' })
    
    buefy.notification.open(notificationOptions)
  }
}

const sendUpdate = async () => {
  try {
    const result = await shopsService.postChain(selectedChain.value)
    chainUpdated.value = true
    
    if (isNew.value) {
      if (typeof result !== 'number' || !Number.isFinite(result) || result <= 0) {
        throw new Error('Missing chain id from UpdateChain response')
      }

      selectedChain.value.chainId = result
      
      // Create chain with required properties for service
      const chainToCreate: ServiceChain = {
        chainId: result,
        chainName: selectedChain.value.chainName || '',
        chainExternalId: selectedChain.value.chainExternalId || '',
        chainGroupId: selectedChain.value.chainGroupId || 0,
        chainGroupName: selectedChain.value.chainGroupName || '',
        shopsRelated: selectedChain.value.shopsRelated || [],
        groupsRelated: selectedChain.value.groupsRelated || [],
      }
      
      shopsService.createChain(chainToCreate)
    } else {
      shopsService.updateChain(selectedChain.value)
    }
    
    openNotification(t('SuccessChainUpdated'), 'is-success')
    emit('chain-update-complete', selectedChain.value)
  } catch (error) {
    const err = error as any
    if (err?.response && err.response.status === 409) {
      Object.getOwnPropertyNames(err.response.data?.errors || {}).forEach(
        (errKey: string) => {
          openNotification(t(errKey), 'is-danger')
        }
      )
    } else {
      openNotification(t('ErrorContactUs'), 'is-danger')
      Sentry.captureException(error)
    }
  }
}

// Lifecycle
onMounted(async () => {
  if (!chainsList.value) {
    await shopsService.getChains()
  }
  
  if (props.id) {
    getChainToEdit()
  } else {
    createNewChain()
  }
})

onBeforeUnmount(() => {
  if (!chainUpdated.value) {
    revertToOriginalChain()
  }
  resetChain()
})
</script>
